import { logger } from "../logger/Logger";
import * as et from "../application/EventTypes";
import { KeyCode } from "./KeyCode";
import { GlobalManagerProvider } from '../application/GlobalManagerMixin';


/**
 * Core interface to add and remove canvas interactions to the viewer.
 *
 * @memberof Autodesk.Viewing
 * @alias Autodesk.Viewing.ToolController
 */
export class ToolController extends GlobalManagerProvider {
  // Public class fields for simple properties
  lastClickX = -1;
  lastClickY = -1;
  scrollInputEnabled = true;
  keyMapCmd = true;

  // By default, any handled event is stopped from further propagation,
  // so the client UI will not receive it anymore. This can prevent unexpected double
  // event handling. However, it may also cause problems, e.g., it makes it impossible for alloy
  // to detect outside clicks for closing open dropdown menus (FLUENT-5378). That's why we make it optional.
  propagateInputEventTypes = [];

  // Private class fields for constants and state
  _kMouseLeft = 0;
  _kMouseRight = 2;

  _kClickThreshold = 2; // Pixels
  _kDoubleClickDelayThreshold = 500; // ms

  _tools = {};
  _toolModality = {};
  _toolStateChangedCbs = {};
  _toolStack = [];
  _lock = false;
  _downX = -1;
  _downY = -1;

  _firefoxLMBfix = false;
  _currentCursor = null;
  _mouseEnabled = false;

  // Save click parameters when clicking with right mouse button
  // and "Left handed mouse setup" is enabled so that we can
  // simulate a double-click with the right mouse button.
  //
  _checkMouseDoubleClick = {};

  /**
   * This class is created internally by the Viewer api and is available via the "toolController" property
   * of the Viewer3D api object. Client implementations should not normally instantiate this class directly.
   *
   * @param {object} viewerImpl - The viewer implementation object.
   * @param {object} viewerApi - The viewer api object.
   * @param {object} autocam - The Autocam interface object.
   * @param {object} utilities - The ViewingUtilities object.
   * @param {object} defaultHandler - The default event handling tool.
   */
  constructor(viewerImpl, viewerApi, autocam, utilities, defaultHandler) {
    super();
    this._viewerImpl = viewerImpl;
    this._viewerApi = viewerApi;
    this._utilities = utilities;
    this._defaultHandler = defaultHandler;

    this.setGlobalManager(this._viewerApi.globalManager);
    this.domElement = this._viewerApi.canvasWrap;
    this.selector = this._viewerImpl.selector;
    this.autocam = autocam;

    // Browser detection (needs to stay in constructor due to runtime evaluation)
    this._isMac = navigator.userAgent.search("Mac OS") !== -1;
    this._isFirefox = navigator.userAgent.search("Firefox") !== -1;
    this._isChrome = navigator.userAgent.search("Chrome") !== -1;
    this._isSafari = navigator.userAgent.search("Safari") !== -1 && !this._isChrome; // Chrome has both Safari and Chrome in the string

    // Create bound method references for event listeners (ES6 class methods lose 'this' context)
    this._boundMousewheel = this.mousewheel.bind(this);
    this._boundKeydown = this.keydown.bind(this);
    this._boundKeyup = this.keyup.bind(this);
    this._boundBlur = this.blur.bind(this);
    this._boundMousedown = this.mousedown.bind(this);
    this._boundDoubleclick = this.doubleclick.bind(this);
    this._boundMousemove = this.mousemove.bind(this);
    this._boundMouseover = this.mouseover.bind(this);

    this.setDefaultTool(this._defaultHandler);

    // If we want to continue listening to mouse movements outside of the window
    // we need to tie our event listener to the window

    this.domElement.addEventListener('mousewheel', this._boundMousewheel, false);
    this.domElement.addEventListener('DOMMouseScroll', this._boundMousewheel, false); // firefox

    //** this.domElement.addEventListener( 'touchstart', function( event ) { this.touchstart( event )}, false );
    //** this.domElement.addEventListener( 'touchmove', function( event ) { this.touchmove( event )}, false );

    this.addWindowEventListener('keydown', this._boundKeydown, false);
    this.addWindowEventListener('keyup', this._boundKeyup, false);
    this.addWindowEventListener('blur', this._boundBlur, false);
  }
  __registerToolByName(tool, toolName)
  {
    this._tools[toolName] = tool;
  }

  /**
   * This method registers an event handling tool with the controller.
   * This makes the tool available for activation and deactivation.
   * Tools are registered under one or more names which must be provided via their "getNames" method.
   * The tools "getNames" method must return an array of one or more names.
   * Typically a tool will only have one name but if it wishes to operate in different modes it can use
   * different names to activate the modes. Registered tools have the properties named
   * "utilities" and "controller" added to them which refer to the ViewingUtilities object and this controller
   * respectively. Tools may not use the name "default" which is reserved.
   *
   * @param {object} tool - The tool to be registered.
   * @param {Function} onStateChanged - callback to be called when disabling the tool through the modality map
   * @alias Autodesk.Viewing.ToolController#registerTool
   */
  registerTool(tool, onStateChanged)
  {
    var names = tool.getNames();

    if (!names || names.length === 0)
    {
      logger.warn("Cannot register tool with no name.");
      return false;
    }
    var registered = false;
    for (var i = 0; i < names.length; ++i)
    {
      if (names[i] !== "default")
      {
        this.__registerToolByName(tool, names[i]);
        const defualtStateChangedCb = (enable) => {
          const name = tool.getName();
          if (enable) {
            this.activateTool(name);
          } else {
            this.deactivateTool(name);
          }
        };
        this._toolStateChangedCbs[names[i]] = onStateChanged && onStateChanged instanceof Function ? onStateChanged : defualtStateChangedCb;
        registered = true;
      }
    }

    tool.utilities = this._utilities;
    tool.controller = this;
    if (tool.register) tool.register();

    return registered;
  }

  /**
   * This method deregisters an event handling tool with the controller afterwhich it will no longer
   * be available for activation and deactivation. All names that the tool is registered under
   * will be deregistered. If any tool is active at the time of deregistration will first be deactivated
   * and it's "deactivate" method will be called.
   *
   * @param {object} tool - The tool to be deregistered.
   * @alias Autodesk.Viewing.ToolController#deregisterTool
   */
  deregisterTool(tool)
  {
    this.isToolActivated(tool.getName()) && this.deactivateTool(tool.getName());

    var names = tool.getNames();

    if (!names || names.length === 0)
    {
      return false;
    }
    for (var i = names.length; --i >= 0;)
    this.__deregisterToolName(names[i]);

    if (tool.deregister) tool.deregister();
    tool.utilities = null;
    tool.controller = null;
    return true;
  }

  __deregisterToolName(name)
  {
    /**
     * @param name
     * @private
     */
    const cleanStack = (name) => {
      for (var i = this._toolStack.length; --i >= 0;)
      if (this._toolStack[i].activeName === name)
      {
        this._tools[name].deactivate(name);
        this._toolStack.splice(i, 1);
      }
    };
    if (name in this._tools)
    {
      cleanStack(name);
      delete this._tools[name];
    }
  }

  /**
   * This method returns the tool registered under the given name.
   *
   * @param {string} name - The tool name to look up.
   * @returns {object} The tool registered under the given name or undefined if not found.
   * @alias Autodesk.Viewing.ToolController#getTool
   */
  getTool(name)
  {
    return this._tools[name];
  }

  /**
   * This method returns the name of the topmost tool on the tool stack.
   * If no tools are active the name of the default tool is returned (which is "default").
   *
   * @returns {string} The tool name to look up.
   * @alias Autodesk.Viewing.ToolController#getActiveToolName
   */
  getActiveToolName()
  {
    var l = this._toolStack.length;
    return l > 0 ? this._toolStack[l - 1].activeName : "default";
  }

  /**
   * This method returns the name of the topmost tool on the tool stack.
   * If no tools are active the name of the default tool is returned (which is "default").
   *
   * @returns {string} The tool name to look up.
   * @alias Autodesk.Viewing.ToolController#getActiveTool
   */
  getActiveTool()
  {
    var l = this._toolStack.length;
    return l > 0 ? this._toolStack[l - 1] : this._tools["default"];
  }

  /**
   * Return the current tool stack.
   *
   * @returns {Array} - list of tools in the tool stack
   * @alias Autodesk.Viewing.ToolController#getActiveTools
   */
  getActiveTools() {
    return this._toolStack.slice();
  }

  isToolActivated(toolName)
  {
    for (var i = 0; i < this._toolStack.length; i++) {

      if (this._toolStack[i].activeName === toolName) {
        return true;
      }
    }
    return false;
  }

  setToolActiveName(toolName)
  {
    var tool = this._tools[toolName];
    if (tool) {
      tool.activeName = toolName;
    }
  }

  /**
   * @param {object} tool - Tool to obtain priority
   */
  _getPriority(tool) {
    return tool.getPriority instanceof Function && tool.getPriority() || 0;
  }

  /**
   * Sorts the toolStack according to the tools' priority.
   * Useful when a tool's priority gets changed after activation.
   *
   * @alias Autodesk.Viewing.ToolController#rearrangeByPriorities
   */
  rearrangeByPriorities() {
    this._toolStack.sort((a, b) => {
      return this._getPriority(a) - this._getPriority(b);
    });
  }

  /**
   * Disables or enables tools in the tool's modality map
   *
   * @param {string} toolName - Name of the tool
   * @alias Autodesk.Viewing.ToolController#activateToolModality
   */
  activateToolModality(toolName) {
    const modalityMap = this.getToolModality(toolName);
    for (let name in modalityMap) {
      const enable = modalityMap[name];
      const stateChangedCb = this._toolStateChangedCbs[name];
      const isActive = this.isToolActivated(name);
      if (enable !== isActive && stateChangedCb) {
        stateChangedCb(enable, name);
      }
    }
  }

  /**
   * Activates the tool registered under the given name. Activation implies pushing the tool
   * on a stack of "active" tools, each of which (starting from the top of the stack) is given
   * the opportunity to handle incoming events. Tools may "consume" events by returning true
   * from their event handling methods, or they may allow events to be passed down to the next tool
   * on the stack by returning false from the handling methods.
   * Upon activation the tools "activate" method is called with the name under which it has been activated.
   * Activation is not allowed while the controller is in a "locked" state (see the methods "setIsLocked"
   * and "getIsLocked"). Tools must be registered prior to activation (see the methods "registerTool"
   * and "deregisterTool").
   * Each tool has its own priority property (default 0), such that a tool with higher priority will get events first.
   *
   * @param {string} toolName - The name of the tool to be activated.
   * @returns {boolean} True if activation was successful.
   * @alias Autodesk.Viewing.ToolController#activateTool
   */
  activateTool(toolName)
  {
    if (this._lock)
    return false;

    var tool = this._tools[toolName];

    this.activateToolModality(toolName);

    if (tool)
    {
      if (tool.count === undefined)
      tool.count = 0;

      var interceptor = null;
      if (this._toolStack.length && this._toolStack[this._toolStack.length - 1].activeName === "intercept") {
        interceptor = this._toolStack.pop();
      }

      var indexToPush = 0;

      for (var i = 0; i < this._toolStack.length; i++) {

        if (this._toolStack[i] === tool) {
          tool.count++;
        }

        if (this._getPriority(this._toolStack[i]) <= this._getPriority(tool)) {
          indexToPush = i + 1;
        }
      }

      tool.activeName = toolName;

      // If the tool belongs to a same instance in tool stack, then don't push it into stack.
      if (tool.count === 0) {
        tool.count++;
        this._toolStack.splice(indexToPush, 0, tool);
      }


      tool.activate(toolName, this._viewerApi);
      if (interceptor) {
        this._toolStack.push(interceptor);
      }

      this._viewerApi.dispatchEvent(
        {
          type: et.TOOL_CHANGE_EVENT,
          toolName: toolName,
          tool: tool,
          active: true
        });
      return true;
    }
    logger.warn("activateTool not found: " + toolName);
    return false;
  }

  /**
   * The first tool found on the active stack with the given name is removed and its "deactivate" method
   * is called. Once deactivated the tool will no longer receive events via its handler methods.
   * Deactivation is not allowed while the controller is in a "locked" state (see the methods "setIsLocked"
   * and "getIsLocked").
   *
   * @param {string} toolName - The name of the tool to be deactivated.
   * @returns {boolean} True if deactivation was successful.
   * @alias Autodesk.Viewing.ToolController#deactivateTool
   */
  deactivateTool(toolName)
  {
    if (this._lock)
    return false;

    for (var i = this._toolStack.length; --i >= 0;)
    {
      if (this._toolStack[i].activeName === toolName)
      {
        const tool = this._tools[toolName];
        if (!tool.count || tool.count <= 1) {
          if (tool.count != 1) {
            console.warn("Deactivating tool without activation: " + toolName);
          }
          this._toolStack.splice(i, 1);
        }

        tool.count--;

        tool.deactivate(toolName);

        this._viewerApi.dispatchEvent(
          {
            type: et.TOOL_CHANGE_EVENT,
            toolName: toolName,
            tool: tool,
            active: false
          });
        return true;
      }
    }
    logger.warn("deactivateTool not found: " + toolName);
    return false;
  }

  /**
   * Obtain a list of all the currently registered tool names.
   *
   * @returns {Array} List of all registered tool names.
   * @alias Autodesk.Viewing.ToolController#getToolNames
   */
  getToolNames()
  {
    return Object.keys(this._tools);
  }

  /**
   * Set the tool which will be requested to handle events if no other active tool handles them.
   *
   * @param {object} tool - The tool to be registered as the default.
   * @alias Autodesk.Viewing.ToolController#setDefaultTool
   */
  setDefaultTool(tool)
  {
    var current = this.getDefaultTool();
    if (tool && tool !== current)
    {
      this.__registerToolByName(tool, "default");
      if (current)
      current.deactivate("default");
      tool.activate("default");
      return true;
    }
    return false;
  }

  /**
   * Get the tool which handle events if no other active tool handles them.
   *
   * @returns {object} The tool to be registered as the default.
   * @alias Autodesk.Viewing.ToolController#getDefaultTool
   */
  getDefaultTool()
  {
    return this._tools["default"];
  }

  /**
   * Set the controller into a locked or unlocked state. While locked, tool activation and deactivation
   * is not allowed. Locking the controller is sometimes necessary to force an interaction to remain
   * active until it is fully completed.
   *
   * @param {boolean} state - The state of the controller lock.
   * @returns {boolean} The previous state of the lock (this may be used to restore the lock
   * to it's previous state).
   * @alias Autodesk.Viewing.ToolController#setIsLocked
   */
  setIsLocked(state)
  {
    var prev = this._lock;
    this._lock = !!state;
    return prev;
  }

  /**
   * Get the current state of the controller lock.
   *
   * @returns {boolean} The state of the lock.
   * @alias Autodesk.Viewing.ToolController#getIsLocked
   */
  getIsLocked()
  {
    return this._lock;
  }

  __checkCursor()
  {
    var cursor = null;
    for (var n = this._toolStack.length; --n >= 0;)
    {
      var tool = this._toolStack[n];
      if (tool.getCursor)
      {
        cursor = tool.getCursor();
        if (cursor)
        break;
      }
    }
    if (!cursor)
    cursor = "auto";

    if (this._currentCursor != cursor)
    {
      this._viewerApi.canvas.style.cursor = cursor;
      this._currentCursor = cursor;
    }
  }

  update(highResTimestamp)
  {
    this.__checkCursor();

    var refresh = false;

    if (this._utilities && this._utilities.update())
    refresh = true;

    for (var n = this._toolStack.length; --n >= 0;)
    {
      var tool = this._toolStack[n];
      if (tool.update && tool.update(highResTimestamp))
      refresh = true;
    }

    this.cameraUpdated = this._viewerApi.navigation.getCamera().dirty;
    refresh ||= this.cameraUpdated;

    refresh && this._viewerApi.navigation.updateCamera();
    return refresh;
  }

  // ------------------------
  // Event handler callbacks:
  // These can use "this".
  __clientToCanvasCoords(event, normalized, screen)
  {
    var rect = this._viewerImpl.getCanvasBoundingClientRect();
    var width = rect.width;
    var height = rect.height;

    // Canvas coordinates: relative to the canvas element.
    // 0 = top left, +ve right and down.
    //
    var canvasX = event.clientX - rect.left;
    var canvasY = event.clientY - rect.top;
    event.canvasX = canvasX;
    event.canvasY = canvasY;

    // Normalized coordinates: [-1, +1].
    // 0 = center, +ve = right and up.
    //
    event.normalizedX = canvasX / width * 2.0 - 1.0;
    event.normalizedY = (height - canvasY) / height * 2.0 - 1.0;

    // Vector: [0, 1].
    // 0 = top left, +ve right and down.
    //
    if (normalized)
    normalized.set(canvasX / width, canvasY / height, 0.0);

    if (screen)
    screen.set(canvasX, canvasY);
  }

  __invokeStack(method, arg1, arg2)
  {
    for (var n = this._toolStack.length; --n >= 0;)
    {
      var tool = this._toolStack[n];

      if (tool && tool[method] && tool[method](arg1, arg2))
      {
        //logger.log(method + " consumed by " + tool.getName() + " = " + arg1.type);
        return true;
      }
    }
    var last = this.getDefaultTool();
    if (last[method] && last[method](arg1, arg2))
    {
      //logger.log(method + " consumed by " + last.getName() + " = " + arg1.type);
      return true;
    }
    return false;
  }

  distributeEvent(methodName, arg1, arg2)
  {
    return this.__invokeStack(methodName, arg1, arg2);
  }

  handleResize()
  {
    this._viewerApi.navigation.setScreenViewport(this._viewerApi.container.getBoundingClientRect());

    // Call handleResize on all tools in case they need it:
    for (var n = this._toolStack.length; --n >= 0;)
    {
      var tool = this._toolStack[n];

      if (tool.handleResize)
      tool.handleResize();
    }
  }

  handleSingleClick(event)
  {
    var button = this.applyButtonMappings(event);
    this.lastClickX = event.clientX;
    this.lastClickY = event.clientY;

    if (this.__invokeStack("handleSingleClick", event, button))
    {
      this.__onEventHandled(event);
    }
  }

  handleDoubleClick(event)
  {
    var button = this.applyButtonMappings(event);

    if (this.__invokeStack("handleDoubleClick", event, button))
    {
      this.__onEventHandled(event);
    }
  }

  handleSingleTap(event)
  {
    this.lastClickX = event.canvasX;
    this.lastClickY = event.canvasY;

    if (this.__invokeStack("handleSingleTap", event))
    {
      this.__onEventHandled(event);
    }
  }

  handleDoubleTap(event)
  {
    this.lastClickX = event.canvasX;
    this.lastClickY = event.canvasY;

    if (this.__invokeStack("handleDoubleTap", event))
    {
      this.__onEventHandled(event);
    }
  }

  handleWheelInput(delta, event)
  {
    if (this.__invokeStack("handleWheelInput", delta, event))
    {
      this.__onEventHandled(event);
    }
  }

  applyButtonMappings(event)
  {
    var button = event.button;

    // Check for Firefox spoof: Control+LMB converted to RMB.
    // The "buttons" property in Firefox will include 1 for LMB and 2 for RMB.
    if ("buttons" in event)
    {
      // This method sometimes gets called more than once with
      // the same event:
      if (event.firefoxSpoof)
      {
        button = this._kMouseLeft;
      }
      // For button down the 1 bit will be on indicating LMB.
      // For button up it's off so check the flag to see if we
      // switched the down event.
      else if (this._firefoxLMBfix && !(event.buttons & 1)) // Button up?
        {
          event.firefoxSpoof = true;
          this._firefoxLMBfix = false;
          button = this._kMouseLeft;
        } else
      if (button === this._kMouseRight && event.buttons & 1)
      {
        button = this._kMouseLeft; // Convert back to reality.
        event.firefoxSpoof = this._firefoxLMBfix = true;
      }
    }
    if (this._viewerApi.navigation.getUseLeftHandedInput())
    {
      button = button === this._kMouseLeft ? this._kMouseRight :
      button === this._kMouseRight ? this._kMouseLeft : button;
    }
    return button;
  }

  applyKeyMappings(event)
  {
    switch (event.keyCode) {

      case KeyCode.EQUALSMOZ:return KeyCode.EQUALS;

      case KeyCode.DASHMOZNEW:
      case KeyCode.DASHMOZ:return KeyCode.DASH;
    }
    return event.keyCode;
  }

  handleKeyDown(event)
  {
    let keyCode;

    if (this.keyMapCmd && this._isMac && (event.code == "MetaLeft" || event.code == "MetaRight")) {
      this.handleCmdKeyEvent(event, 'keydown');
      return;
    } else
    {
      keyCode = this.applyKeyMappings(event);
    }

    if (keyCode && this.__invokeStack("handleKeyDown", event, keyCode))
    {
      this.__onEventHandled(event);
    }
  }

  handleKeyUp(event)
  {
    let keyCode;

    if (this.keyMapCmd && this._isMac && (event.code == "MetaLeft" || event.code == "MetaRight")) {
      this.handleCmdKeyEvent(event, 'keyup');
      return;
    } else
    {
      keyCode = this.applyKeyMappings(event);
    }

    if (keyCode && this.__invokeStack("handleKeyUp", event, keyCode))
    {
      this.__onEventHandled(event);
    }
  }

  /**
   * Catches a CMD event and generate a Control one instead.
   * @param {object} cmdEvent - Event generated by CMD key
   * @param {string} eventType - Event type
   * @alias Autodesk.Viewing.ToolController#handleCmdKeyEvent
   */
  handleCmdKeyEvent(cmdEvent, eventType) {
    this.__onEventHandled(cmdEvent);

    var ev = new KeyboardEvent(eventType, {
      "key": "Control", //optional and defaulting to "", of type DOMString, that sets the value of KeyboardEvent.key.
      "code": cmdEvent.code == "MetaLeft" ? "ControlLeft" : "ControlRight", //optional and defaulting to "", of type DOMString, that sets the value of KeyboardEvent.codevent.
      "location": cmdEvent.location, //optional and defaulting to 0, of type unsigned long, that sets the value of KeyboardEvent.location.
      "ctrlKey": true, //optional and defaulting to false, of type Boolean, that sets the value of KeyboardEvent.ctrlKey.
      "metaKey": false, //optional and defaulting to false, of type Boolean, that sets the value of KeyboardEvent.metaKey.
      "repeat": cmdEvent.repeat, //optional and defaulting to false, of type Boolean, that sets the value of KeyboardEvent.repeat.
      "isComposing": cmdEvent.isComposing, //optional and defaulting to false, of type Boolean, that sets the value of KeyboardEvent.isComposing.
      "charCode": 0, //optional and defaulting to 0, of type unsigned long, that sets the value of the deprecated KeyboardEvent.charCodevent.
      "keyCode": 17, //optional and defaulting to 0, of type unsigned long, that sets the value of the deprecated KeyboardEvent.keyCode.
      "which": 17 //optional and defaulting to 0, of type unsigned long, that sets the value of the deprecated KeyboardEvent.which.
    });

    window.dispatchEvent(ev);
  }

  handleButtonDown(event, button)
  {
    if (this.__invokeStack("handleButtonDown", event, button))
    {
      this.__onEventHandled(event);
    }
  }

  handleButtonUp(event, button)
  {
    if (this.__invokeStack("handleButtonUp", event, button))
    {
      this.__onEventHandled(event);
    }
  }

  handleMouseMove(event)
  {
    if (this.__invokeStack("handleMouseMove", event))
    {
      this.__onEventHandled(event);
    }
  }

  handleBlur(event)
  {
    if (this.__invokeStack("handleBlur", event))
    {
      this.__onEventHandled(event);
    }
  }

  reportSelectionChange(reportingTool) {
    if (!reportingTool) {
      console.warn('reportSelectionChange: reportingTool is not provided');
      return;
    }

    for (let n = this._toolStack.length; --n >= 0;) {
      const tool = this._toolStack[n];
      if (tool !== reportingTool && tool.handleSelectionChange) {
        tool.handleSelectionChange();
      }
    }
  }

  // ====================================================
  // Event handlers:
  keydown(event)
  {
    if (ToolController._lastTouchedElement && !this.domElement.contains(ToolController._lastTouchedElement)) {
      return;
    }
    var _document = this.getDocument();
    if (_document.activeElement instanceof HTMLInputElement ||
    _document.activeElement instanceof HTMLTextAreaElement ||
    _document.activeElement instanceof HTMLSelectElement) {
      return;
    }

    // Support for HTML5 editable divs
    if (_document.activeElement) {
      var divIsEditable = _document.activeElement.getAttribute('contenteditable');
      if (divIsEditable === 'true' || divIsEditable === '') {
        // TODO: Proper handle of value 'inherit'
        return;
      }
    }

    this.handleKeyDown(event);
  }

  keyup(event)
  {
    this.handleKeyUp(event);
  }

  /**
   * @param {object} button - button
   * @param {object} event - event
   * @returns {boolean} - should check for double-click event?
   */
  _shouldCheckDoubleClick(button, event) {
    return this._viewerApi.navigation.getUseLeftHandedInput() && button === 0 || this._isFirefox && button === 1 ||
    this._isMac && !this._isSafari && button === 0 && event.ctrlKey;
  }

  mousedown(event)
  {
    var _document = this.getDocument();
    _document.activeElement && _document.activeElement.blur && _document.activeElement.blur();

    this.__clientToCanvasCoords(event);

    var buttonDown = this.applyButtonMappings(event);
    this.handleButtonDown(event, buttonDown);

    this._downX = event.canvasX;
    this._downY = event.canvasY;

    if (this._shouldCheckDoubleClick(buttonDown, event))
    {
      var cmdc = this._checkMouseDoubleClick;

      var delayOK = cmdc.time !== undefined &&
      event.timeStamp - cmdc.time < this._kDoubleClickDelayThreshold;

      var positionOK = cmdc.x !== undefined && cmdc.y !== undefined &&
      Math.abs(cmdc.x - event.canvasX) <= this._kClickThreshold &&
      Math.abs(cmdc.y - event.canvasY) <= this._kClickThreshold;

      if (!delayOK || !positionOK || cmdc.clickCount && 2 <= cmdc.clickCount) {
        cmdc.clickCount = 0;
      }

      if (!cmdc.clickCount) {
        cmdc.clickCount = 1;
        cmdc.x = event.canvasX;
        cmdc.y = event.canvasY;
        cmdc.time = event.timeStamp;

      } else if (cmdc.clickCount === 1) {
        cmdc.clickCount = 2;
      }
    }

    /**
     * @param {object} event - Event to handle
     */
    const handleUp = (event) => {
      var buttonUp = this.applyButtonMappings(event);
      if (buttonUp === buttonDown)
      {
        this.removeDocumentEventListener('mouseup', handleUp);
        this.mouseup(event);
      }
    };

    this.addDocumentEventListener('mouseup', handleUp, false);

    this.registerWindowMouseMove();
  }

  mousemove(event)
  {
    this.__clientToCanvasCoords(event);

    var deltaX = this._downX - event.canvasX;
    var deltaY = this._downY - event.canvasY;
    if (Math.abs(deltaX) > this._kClickThreshold || Math.abs(deltaY) > this._kClickThreshold)
    {
      this._downX = -1;
      this._downY = -1;
    }
    this.handleMouseMove(event);
  }

  mouseup(event)
  {
    this.__clientToCanvasCoords(event);

    var buttonUp = this.applyButtonMappings(event);
    this.handleButtonUp(event, buttonUp);

    var deltaX = this._downX - event.canvasX;
    var deltaY = this._downY - event.canvasY;

    this._downX = -1;
    this._downY = -1;

    if (Math.abs(deltaX) <= this._kClickThreshold && Math.abs(deltaY) <= this._kClickThreshold)
    this.handleSingleClick(event);

    if (this._shouldCheckDoubleClick(buttonUp, event))
    {
      var cmdc = this._checkMouseDoubleClick;
      if (cmdc.clickCount === 2) {
        this.handleDoubleClick(event);

        cmdc.clickCount = 0;
        cmdc.x = undefined;
        cmdc.y = undefined;
        cmdc.time = undefined;
      }
    }

    this.unregisterWindowMouseMove();
  }

  doubleclick(event)
  {
    this.__clientToCanvasCoords(event);

    this._downX = event.canvasX;
    this._downY = event.canvasY;

    this.handleDoubleClick(event);
  }

  mousewheel(event)
  {
    if (!this.scrollInputEnabled) {
      return;
    }

    // A mousewheel event also contains coordinates of the mouse position, so we make them available as canvas coords too.
    this.__clientToCanvasCoords(event);

    var delta = 0;

    if (event.wheelDelta) {// WebKit / Opera / Explorer 9
      delta = event.wheelDelta / 40;
    } else
    if (event.detail) {// Firefox
      delta = -event.detail;
    } else
    if (event.deltaY) {// Firefox / Explorer + event target is SVG.
      var factor = this._isFirefox ? 1 : 40;
      delta = -event.deltaY / factor;
    }

    this.handleWheelInput(delta, event);
  }

  blur(event)
  {
    this.handleBlur(event);
  }

  // ??? this.domElement.addEventListener( 'contextmenu', function ( event ) { event.preventDefault(); }, false );
  mouseover(e)
  {
    ToolController._lastTouchedElement = e.target;
    // ??? if (ToolController._lastTouchedElement != viewerImpl.canvas) this.autoMove(-1, false)
  }

  // to maintain drag continuity outside the canvas element
  // move mousemove/over listeners from canvas to window
  registerWindowMouseMove() {
    this.addWindowEventListener('mousemove', this._boundMousemove);
    this.addWindowEventListener('mouseover', this._boundMouseover);
    this.domElement.removeEventListener('mousemove', this._boundMousemove);
    this.domElement.removeEventListener('mouseover', this._boundMouseover);
  }

  unregisterWindowMouseMove() {
    this.removeWindowEventListener('mousemove', this._boundMousemove);
    this.removeWindowEventListener('mouseover', this._boundMouseover);
    this.domElement.addEventListener('mousemove', this._boundMousemove);
    this.domElement.addEventListener('mouseover', this._boundMouseover);
  }

  enableMouseButtons(state)
  {
    if (state && !this._mouseEnabled)
    {
      this.domElement.addEventListener('mousedown', this._boundMousedown);
      this.domElement.addEventListener('dblclick', this._boundDoubleclick);
      this.domElement.addEventListener('mousemove', this._boundMousemove);
      this.domElement.addEventListener('mouseover', this._boundMouseover);
    } else
    if (!state && this._mouseEnabled)
    {
      this.domElement.removeEventListener('mousedown', this._boundMousedown);
      this.domElement.removeEventListener('dblclick', this._boundDoubleclick);
      this.domElement.removeEventListener('mousemove', this._boundMousemove);
      this.domElement.removeEventListener('mouseover', this._boundMouseover);
    }
    var returnValue = this._mouseEnabled;
    this._mouseEnabled = state;

    return returnValue;
  }

  uninitialize() {
    // remove them all just to be sure, doesn't hurt
    this.domElement.removeEventListener('mousemove', this._boundMousemove);
    this.domElement.removeEventListener('mouseover', this._boundMouseover);
    this.removeWindowEventListener('mousemove', this._boundMousemove);
    this.removeWindowEventListener('mouseover', this._boundMouseover);

    this.removeWindowEventListener('keydown', this._boundKeydown);
    this.removeWindowEventListener('keyup', this._boundKeyup);
    this.removeWindowEventListener('blur', this._boundBlur);

    this.domElement = null;
    this.selector = null;
    this.autocam = null;

    this._lock = false; // Critical to unlock before deactivating all tools, so we'll be able actually deactivate them.

    // Deactivate all active tools
    while (this._toolStack.length > 0)
    this.deactivateTool(this._toolStack[this._toolStack.length - 1].activeName);
    this._tools = null;
    this._toolStack = null;
    this._utilities = null;
    this._viewerApi = null;
    this._viewerImpl = null;
    ToolController._lastTouchedElement = null;
  }

  // Compatibility methods. TODO: eliminate these
  set2DMode() {}

  setAutocam() {}

  syncCamera() {}

  // TODO: implement this in navapi - then set autocam home from navapi values
  recordHomeView()
  {
    var camera = this._viewerApi.navigation.getCamera();
    this.autocam.sync(camera);
    this.autocam.setHomeViewFrom(camera);
  }

  /**
   * Whether mouse scroll wheel (and/or two-finger vertical swipe) will trigger a camera zoom operation.
   *
   * @param {boolean} isEnabled
   * @alias Autodesk.Viewing.ToolController#setMouseWheelInputEnabled
   */
  setMouseWheelInputEnabled(isEnabled) {
    this.scrollInputEnabled = isEnabled;
  }

  /**
   * Set the modality map for each tool. This function will clear any existing modality map.
   * The map object consists of a key which represents the tool name and a value which is an object that represents which tools to enable and disable.
   * To get the tool names @see {@link Autodesk.Viewing.ToolController#getToolNames}
   *
   * @example
   *   // When enabling the measure tool the explode, bimwalk and section tools will be disabled.
   *   // When enabling the section tool the pan tool will be disabled.
   *   toolController.setModalityMap({
   *       measure: { explode: false, bimwalk: false, section: false },
   *       section: { pan: false }
   *   });
   * @param {object} map - Object describing each tool's modality with other tools.
   * @alias Autodesk.Viewing.ToolController#setModalityMap
   */
  setModalityMap(map) {
    // Clear the existing modality map
    this._toolModality = {};
    for (let name in map) {
      this.setToolModality(name, map[name]);
    }
  }

  /**
   * Get the modality map.
   *
   * @returns {object} - returns a copy of the tool modality.
   * @alias Autodesk.Viewing.ToolController#getModalityMap
   */
  getModalityMap() {
    return Object.assign({}, this._toolModality);
  }

  /**
   * Get a specific tool's modality map.
   *
   * @param {string} name - tool name
   * @returns {object} - an object describing the tool's modality map.
   * @alias Autodesk.Viewing.ToolController#getToolModality
   */
  getToolModality(name) {
    return this._toolModality[name];
  }

  /**
   *
   * @param {string} name - Tool name that is associated with the modality map.
   * @param {object} map - Object containing the name of the tool as the key and a boolean that describes if the tool can be enabled or disabled.
   * @alias Autodesk.Viewing.ToolController#setToolModality
   */
  setToolModality(name, map) {
    if (!name) {
      logger.warn('Cannot add tool modality. Missing tool name.');
      return;
    }

    if (!map) {
      logger.warn('Cannot add tool modality. Missing tool map.');
      return;
    }

    this._toolModality[name] = map;
  }

  // Called if an input event was handled by a tool. In this case, we stop any further event processing (unless disabled by the client).
  __onEventHandled(event) {
    if (!this.propagateInputEventTypes.includes(event.type)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

}

// Private static variable to store which viewer basically should process keyboard events
Object.defineProperty(ToolController, '_lastTouchedElement', { value: null, writable: true });